import { getDropTargetsObjects } from './getDropTargetsObjects';
import { DropTargetObjects, LayoutItem } from './types';

export const checkDropTargetsParent = (dropTargets: DropTargetObjects[]) => {
  if (dropTargets.length === 0) return false;
  const parent = dropTargets[0].ref.current?.parentElement;
  if (!parent) return false;
  return dropTargets.every(
    ({ ref: { current } }) => current?.parentElement === parent
  );
};

export type DropTarget = NonNullable<
  ReturnType<typeof getDropTargetsObjects>['dropTargetsObjects'][0]
>;

export const getRowsAndColumnsCount = (dropTragets: DropTarget[]) => {
  const columnStart = new Set<number>();
  const rowStart = new Set<number>();
  dropTragets.forEach(({ box: { x, y } }) => {
    columnStart.add(x);
    rowStart.add(y);
  });
  return { columnCount: columnStart.size, rowCount: rowStart.size };
};

// Assumption is that we have a consitent horizontal and vertical spacing
// between items and consitent horizontal and vertical spacing to the
// container.
export const getDeltas = (
  dropTargets: DropTarget[],
  { columnCount, rowCount }: { columnCount: number; rowCount: number }
) => {
  if (dropTargets.length === 0 || dropTargets.length === 1) {
    return { deltaX: 0, deltaY: 0 };
  }
  const [box1FromTheSameRow, box2FromTheSameRow] =
    columnCount > 1
      ? dropTargets.reduce<DOMRect[]>((row, { box }) => {
          if (row.length > 1) return row;
          const [firstBox] = row;
          if (!firstBox || firstBox.y < box.y) return [box];
          return [...row, box];
        }, [])
      : [];
  const deltaX =
    box1FromTheSameRow && box2FromTheSameRow
      ? box2FromTheSameRow.x - (box1FromTheSameRow.x + box1FromTheSameRow.width)
      : 0;
  const { box: box0 } = dropTargets[0];
  const { box: nextRowBox } =
    dropTargets.find(({ box }) => box.y > box0.y) ?? {};
  const deltaY =
    nextRowBox && rowCount > 1 ? nextRowBox.y - (box0.y + box0.height) : 0;
  return { deltaX, deltaY };
};

export const getGhostContainer = (
  helperContainer?: HTMLElement | (() => HTMLElement)
) => {
  if (helperContainer instanceof HTMLElement) return helperContainer;
  return (
    (typeof helperContainer === 'function' && helperContainer()) ||
    document.body
  );
};

export const swap = (start: number, end: number, dropTargets: DropTarget[]) => {
  if (start === end) return false;
  dropTargets[start].currentIndex = dropTargets[end].currentIndex;
  const delta = start < end ? -1 : 1;
  const loopStart = start < end ? start + 1 : end;
  const loopEnd = start < end ? end : start - 1;
  for (let i = loopStart; i <= loopEnd; i++) {
    dropTargets[i].currentIndex += delta;
  }
  dropTargets.sort(
    ({ currentIndex: currentIndex1 }, { currentIndex: currentIndex2 }) =>
      currentIndex1 - currentIndex2
  );
  return true;
};

export const updateLayoutValues = <T extends LayoutItem>(
  layoutItem: T,
  currentX: number,
  currentY: number
) => {
  layoutItem.currentX = currentX;
  layoutItem.currentY = currentY;
  layoutItem.translateX = currentX - layoutItem.relativeX;
  layoutItem.translateY = currentY - layoutItem.relativeY;
};
